import React from "react";
import {
  Image,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  Platform,
  useWindowDimensions,
  View,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import Ionicons from "react-native-vector-icons/Ionicons";
import { useNavigation, useRoute, RouteProp } from "@react-navigation/native";
import { useTheme } from "../context/ThemeContext";
import { usePlayer } from "../context/PlayerContext";
import { getThemeColors } from "../utils/themeStyles";
import MissionControllers from "../components/MissionControllers";
import MissionPrerequisites from "../components/MissionPrerequisites";
import { useMissions } from "../context/MissionsContext";
import RemoteImage from "../components/RemoteImage";
import { azuriteFallback } from "../config/env";
import { useT } from '../i18n/useT';
import { useMissionLaunch } from '../hooks/useMissionLaunch';
import { fetchMissionRun, managerReset } from '../api/missionRun';

type MissionDetailRouteParams = {
  MissionDetail: { missionId: number };
};

// ⚠️ Il y avait ICI trois tables codées en dur (`STORY_KEY_MAP`, `STORY_SYNOPSES`,
// `LEARNING_OBJECTIVES`, ~200 lignes) qui fabriquaient le synopsis et les objectifs à partir
// d'une « clé d'histoire » déduite de l'id de mission. Elles ont été supprimées (CCT-253) :
//   - elles étaient devenues FAUSSES (la table faisait pointer la mission 2 sur
//     « foundation_gamble », alors que c'est « Architecture Under Fire ») ;
//   - toute mission absente de la table retombait sur « egocrisis » — donc affichait le
//     synopsis d'une AUTRE histoire, sans que rien ne le signale ;
//   - une mission créée dans le BO n'y figurait par construction jamais.
// Le synopsis, les objectifs et l'équipe viennent désormais de la base : `/me/stories` quand
// on est connecté, `/public/marketplace/stories` pour le visiteur. Quand rien n'est authoré,
// on l'AFFICHE au lieu d'inventer.

export default function MissionDetailScreen() {
  const t = useT();
  // Démarrage EXPLICITE (CCT-286) : la langue se choisit ici, avant d'entrer — après, elle
  // est figée sur la partie et le serveur refuserait de la changer.
  const { launch, dialog: launchDialog } = useMissionLaunch();
  const navigation = useNavigation<any>();
  const route = useRoute<RouteProp<MissionDetailRouteParams, "MissionDetail">>();
  // ⚠️ Deep-link / rafraîchissement : l'id vient de l'URL (`/mission/21`) en CHAÎNE, alors que
  // `mission.id` est un NOMBRE. Sans cette normalisation, le `===` échouait et la page affichait
  // « Mission introuvable » — invisible en navigation in-app, où le param est déjà numérique.
  const missionId = Number((route.params as any)?.missionId);

  // ⚠️ THE CLICK OPENS THE PAGE, THEN THE SYNCHRONISATION BECOMES VISIBLE. The catalogue's
  // "open" buttons called `launch` BEFORE navigating: the synchronisation (language, controllers,
  // engine start) took several seconds during which the screen did not change. The user clicked
  // again, starting a SECOND synchronisation, and the page only opened at the end. Arriving here
  // first gives the click an immediate effect, and `launch`'s dialog becomes what it should always
  // have been: a MODAL wait, on a page that is already open.
  //
  // ⚠️ THE PARAMETER IS CONSUMED, NOT MERELY GUARDED. A ref only lasts as long as the mount:
  // opening the notification centre and coming back remounted this screen, `autoLaunch` was still
  // in the route, and the whole synchronisation ran again. That is the reported defect, and it is
  // mine. Clearing the parameter makes the intent one-shot whatever the navigator does with the
  // component.
  //
  // ⚠️ THE REF STAYS TOO. `setParams` is asynchronous: between the launch and the re-render
  // carrying the cleared parameter, a second render would fire the effect once more.
  const autoLaunch = Boolean((route.params as any)?.autoLaunch);
  const dejaLance = React.useRef(false);
  React.useEffect(() => {
    if (!autoLaunch || dejaLance.current || !Number.isFinite(missionId)) return;
    dejaLance.current = true;
    navigation.setParams({ autoLaunch: false } as never);
    void launch(missionId, () => navigation.navigate("ChapterDetail", { missionId, chapterId: 1 }));
  }, [autoLaunch, missionId, launch, navigation]);
  const { isDarkTheme } = useTheme();
  const colors = getThemeColors(isDarkTheme);
  // ⚠️ HOOK : doit rester AVANT le retour anticipé « Mission introuvable », sinon l'ordre des
  // hooks change d'un rendu à l'autre et React casse l'écran (erreur console, pas d'écran blanc).
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const narrow = windowWidth < 700;
  const { playerName, playerAvatar } = usePlayer();
  // Fiche du personnage ouverte au clic (CCT-152) : nom, rôle, description authorée.
  const [charCard, setCharCard] = React.useState<any | null>(null);
  // CCT-341 — THE AUTHOR'S TOOLS. `canManage` is decided by the SERVER (the mission's author, an
  // admin of its company, a global admin): re-deriving it here would show two destructive buttons
  // to someone not entitled to them, and the server would then refuse with no visible reason.
  // Absent means false — an older server grants nothing.
  const [gestion, setGestion] = React.useState(false);
  const [reinit, setReinit] = React.useState<null | "recharger" | "reinitialiser">(null);
  const [enCours, setEnCours] = React.useState(false);
  React.useEffect(() => {
    let vivant = true;
    void fetchMissionRun(null, missionId).then((r) => {
      if (vivant) setGestion(Boolean(r?.canManage));
    });
    return () => { vivant = false; };
  }, [missionId]);
  const { activeMissions, trialMissions, allMarketplaceMissions, startTrial, addToCart, isInTrial, isInCart } = useMissions();

  // Find mission from any source
  const activeMission = activeMissions.find((m) => m.id === missionId);
  const trialMission = trialMissions.find((m) => m.id === missionId);
  const marketMission = allMarketplaceMissions.find((m) => m.id === missionId);
  const mission = activeMission ?? trialMission ?? marketMission;

  // CCT-117 — ÉQUIPE de la mission, servie par la MÊME source que le reste de la fiche :
  // `/me/stories` pour l'apprenant, `/public/marketplace/stories` pour le visiteur déconnecté.
  const characters: any[] = ((mission as any)?.characters ?? []).map((c: any) => ({
    name: c.name, role: c.role ?? "",
    avatar: c.avatarUrl ? { uri: c.avatarUrl } : null,
    isPlayer: !!c.isPlayer,       // CCT-151 : le personnage joué par l'apprenant
    description: c.description ?? "",
  }));
  // CCT-107 — textes AUTHORÉS dans le BO (réglages de mission). Plus aucun repli : une fiche
  // sans synopsis doit se voir, pas emprunter celui d'une autre histoire.
  const authoredSynopsis = ((mission as any)?.synopsis ?? '').trim();
  const synopsisParas: string[] = authoredSynopsis
    ? authoredSynopsis.split(/\n\s*\n/).map((p: string) => p.trim()).filter(Boolean)
    : [];
  const objectives: string[] = (mission as any)?.objectives ?? [];

  // QUI montrer pour un personnage — règle UNIQUE (CCT-255). Sur le personnage INCARNÉ, on montre
  // le JOUEUR quand il est connecté (son avatar de profil, son nom) : lui présenter « Samir »
  // comme étant lui revient à lui montrer un inconnu. DÉCONNECTÉ, il n'y a pas de joueur : on
  // garde le personnage authoré, qui est précisément le rôle proposé au visiteur.
  // Cette règle servait la grille « Personnages » ; le montage de bannière la refaisait à moitié
  // (avatar du personnage, jamais celui du joueur) — d'où deux visages pour la même personne sur
  // le même écran.
  const visage = (char: any) => {
    const moi = !!char.isPlayer && !!playerName;
    return {
      moi,
      avatar: moi ? (playerAvatar ?? char.avatar) : char.avatar,
      nom: moi ? playerName : char.name,
    };
  };

  // SURVIVRE AU RAFRAÎCHISSEMENT dans le stack public (CCT-255). Cet écran n'a pas de chemin
  // `linking.ts` côté public — on ne peut pas en déclarer un second sur le même motif sans
  // « Found conflicting screens with the same pattern », qui tue l'application au démarrage.
  // React-navigation écrit donc le NOM de route (`/MissionDetail`), comme il le fait déjà pour
  // `/PublicMissions` et `/Login` (CCT-84), et l'id disparaît de l'URL : au F5, la fiche
  // affichait « Mission introuvable ». On mémorise donc la mission ouverte, et `app/index.tsx`
  // la relit pour reconstruire les paramètres initiaux.
  //
  // Écarté : réécrire l'URL en `/mission/{id}` après le montage — react-navigation la réécrit
  // ensuite depuis l'état de navigation, notre correction ne tenait pas.
  React.useEffect(() => {
    if (Platform.OS !== 'web' || !Number.isFinite(missionId)) return;
    try {
      sessionStorage.setItem('ccjaw.missionDetail', String(missionId));
    } catch {
      /* pas de sessionStorage : le F5 rouvrira le catalogue, sans casse */
    }
  }, [missionId]);

  // Essai gratuit : côté serveur il crée un Assignment, donc il EXIGE un compte. En déconnecté,
  // `startTrial` retournait en silence — le bouton paraissait mort (CCT-253).
  const beginTrial = async () => {
    const issue = await startTrial(missionId);
    if (issue === 'needsLogin') {
      try {
        sessionStorage.setItem('ccjaw.returnTo', `/mission/${missionId}`);
      } catch {
        /* pas de sessionStorage (natif) : on ouvre simplement la connexion */
      }
      navigation.navigate('Login');
    }
  };

  if (!mission) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]}>
        <TouchableOpacity style={styles.backBtn} onPress={() => navigation.goBack()}>
          <Ionicons name="arrow-back" size={24} color={colors.textPrimary} />
        </TouchableOpacity>
        <View style={{ flex: 1, justifyContent: "center", alignItems: "center" }}>
          <Text style={{ color: colors.textSecondary }}>{t('mission.notFound')}</Text>
        </View>
      </SafeAreaView>
    );
  }

  // CCT-113/118 — bannière par SURFACE. Le cadre doit avoir le MÊME ratio que l'image servie :
  // un cadre 21/9 plafonné à 250 px de haut donnait, sur un écran large, une boîte ~4.8:1 dans
  // laquelle « cover » rognait violemment (bannière tronquée). On garde donc le ratio du format
  // choisi et on borne la LARGEUR, pas la hauteur.
  const banner = (narrow ? (mission as any).bannerMobile : (mission as any).bannerWeb)
    ?? (mission as any).coverImage;
  const bannerRatio = narrow ? 16 / 9 : 1536 / 640;
  const coverImage = banner;
  // ⚠️ HAUTEUR PLAFONNÉE (CCT-255). Le ratio seul décide de la hauteur : sur un écran large,
  // 1536/640 donne une bannière de ~660 px qui, avec l'en-tête, le titre et les boutons,
  // repoussait TOUT le contenu sous la ligne de flottaison. Le visiteur devait décider d'acheter
  // ou d'essayer sans avoir lu une ligne du synopsis. On borne donc à ~⅓ de la fenêtre : le
  // rognage qui en résulte est délibéré (le cadrage fin se fait dans la Gallery, CCT-110).
  const bannerMaxH = Math.round(Math.min(windowHeight * 0.34, 340));

  // Équipe montrée à la place d'une bannière absente : le JOUEUR d'abord (c'est le rôle proposé
  // au visiteur), puis les autres, 5 au plus — au-delà les pastilles se chevauchent en bouillie.
  const montageEquipe = [...characters].sort((a, b) => Number(!!b.isPlayer) - Number(!!a.isPlayer))
    .slice(0, 5);
  const certification = (mission as any).certification ?? (marketMission?.tags?.certification ?? "");
  const inTrial = isInTrial(missionId);
  const inCart = isInCart(missionId);
  const isActive = !!activeMission;
  const isTrial = !!trialMission;

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]}>
      {/* Back button */}
      <TouchableOpacity style={[styles.backBtn, { backgroundColor: colors.overlay }]} onPress={() => navigation.goBack()}>
        <Ionicons name="arrow-back" size={22} color="#FFF" />
      </TouchableOpacity>

      <ScrollView showsVerticalScrollIndicator={false}>
        {/* Banner : blob distant en priorité, sinon banner LOCAL (par nom), sinon montage personnages. */}
        <RemoteImage
          uris={[
            typeof coverImage === "string" ? coverImage : coverImage?.uri,
            azuriteFallback(typeof coverImage === "string" ? coverImage : coverImage?.uri),
          ]}
          style={[styles.banner, { aspectRatio: bannerRatio, maxHeight: bannerMaxH }]}
          // `contain` laissait des BANDES (la bannière ne remplissait pas la largeur) : on
          // remplit le cadre. Le rognage propre se fait désormais dans la GALLERY (CCT-110).
          resizeMode="cover"
          renderFallback={() => (
            <View style={[styles.bannerPlaceholder, { height: bannerMaxH, backgroundColor: isDarkTheme ? "#1A1F2E" : "#1E293B" }]}>
              {/* Bannière absente : on montre L'ÉQUIPE de l'histoire (CCT-255), joueur compris —
                  son avatar de profil s'il est connecté, sinon le personnage désigné par
                  l'auteur. Une bannière vide ne dit rien de la mission ; des visages, si. */}
              <View style={styles.bannerAvatarRow}>
                {montageEquipe.map((char: any, idx: number) => {
                  const { moi, avatar, nom } = visage(char);
                  return (
                    <View
                      key={`${nom}-${idx}`}
                      style={[styles.bannerAvatarCircle, idx > 0 && styles.bannerAvatarOverlap,
                              moi && { borderColor: colors.primary }]}
                    >
                      {avatar
                        ? <Image source={avatar} style={styles.bannerAvatarImg} resizeMode="cover" />
                        : <View style={[styles.bannerAvatarImg, { alignItems: "center", justifyContent: "center", backgroundColor: colors.surfaceSecondary }]}>
                            <Text style={{ color: colors.textSecondary, fontWeight: "700" }}>{(nom || "?").slice(0, 1)}</Text>
                          </View>}
                    </View>
                  );
                })}
              </View>
              {montageEquipe.length === 0 && (
                <Ionicons name="images-outline" size={48} color={"rgba(255,255,255,0.3)"} />
              )}
            </View>
          )}
        />

        <View style={styles.body}>
          {/* Title & badge */}
          <View style={styles.titleRow}>
            <View style={{ flex: 1 }}>
              <Text style={[styles.missionTitle, { color: colors.textPrimary }]}>{mission.title}</Text>
              {"subtitle" in mission && mission.subtitle ? (
                <Text style={[styles.subtitle, { color: colors.textSecondary }]}>{mission.subtitle as string}</Text>
              ) : null}
            </View>
            {certification ? (
              <View style={[styles.certBadge, { backgroundColor: colors.primary }]}>
                <Text style={styles.certBadgeText}>{certification}</Text>
              </View>
            ) : null}
          </View>

          {/* Synopsis — masqué si l'auteur n'en a pas écrit ET qu'aucun texte bundlé ne
              correspond : un titre de section seul (sans paragraphe) fait « page cassée ». */}
          {synopsisParas.length > 0 && (
            <View style={[styles.section, { borderBottomColor: colors.border }]}>
              <Text style={[styles.sectionTitle, { color: colors.textPrimary }]}>{t('missionDetail.synopsis')}</Text>
              {synopsisParas.map((para: string, i: number) => (
                <Text key={i} style={[styles.synopsisPara, { color: colors.textSecondary }]}>
                  {para}
                </Text>
              ))}
            </View>
          )}

          <MissionPrerequisites text={mission.prerequisites} requiresP1={mission.requires_entra_p1} />

          {/* Characters */}
          {characters.length > 0 && (
            <View style={[styles.section, { borderBottomColor: colors.border }]}>
              <Text style={[styles.sectionTitle, { color: colors.textPrimary }]}>{t('missionDetail.characters')}</Text>
              <View style={styles.charsGrid}>
                {characters.map((char: any) => {
                  // CCT-152 — sur la carte du personnage INCARNÉ, on montre le JOUEUR : son
                  // avatar et son nom. Voir « Samir » à sa place revenait à lui présenter un
                  // inconnu comme étant lui. Le nom du personnage reste affiché juste en
                  // dessous : c'est le RÔLE qu'il tient, et il ouvre sa fiche au clic.
                  const { moi: me, avatar: shownAvatar, nom: shownName } = visage(char);
                  return (
                    <TouchableOpacity key={char.name} style={styles.charItem} activeOpacity={0.8}
                      onPress={() => setCharCard(char)}>
                      {shownAvatar
                        ? <Image source={shownAvatar} style={[styles.charAvatar, me && styles.charAvatarMe]} />
                        : <View style={[styles.charAvatar, me && styles.charAvatarMe,
                            { alignItems: "center", justifyContent: "center", backgroundColor: colors.surfaceSecondary }]}>
                            <Text style={{ color: colors.textSecondary }}>{shownName.slice(0, 1)}</Text>
                          </View>}
                      <Text style={[styles.charName, { color: me ? colors.primary : colors.textPrimary }]} numberOfLines={2}>
                        {shownName}
                      </Text>
                      {/* CCT-153 : le joueur TIENT ce rôle — c'est donc le rôle du personnage
                          qui le décrit (« Ingénieur Data — Pipeline Facturation »), pas une
                          périphrase. Le nom du personnage reste dans sa fiche, au clic. */}
                      <Text style={[styles.charRole, { color: colors.textSecondary }]} numberOfLines={2}>
                        {char.role}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
            </View>
          )}

          {/* Learning objectives */}
          {objectives.length > 0 && (
            <View style={[styles.section, { borderBottomColor: colors.border }]}>
              <Text style={[styles.sectionTitle, { color: colors.textPrimary }]}>{t('missionDetail.objectives')}</Text>
              {objectives.map((obj, i) => (
                <View key={i} style={styles.objRow}>
                  <Ionicons name="checkmark-circle" size={16} color={colors.success} style={{ marginTop: 2 }} />
                  <Text style={[styles.objText, { color: colors.textSecondary }]}>{obj}</Text>
                </View>
              ))}
            </View>
          )}

          {/* Action buttons */}
          <View style={styles.actions}>
            {isActive && (
              <TouchableOpacity
                style={[styles.primaryBtn, { backgroundColor: colors.primary }]}
                onPress={() => { void launch(missionId, () => navigation.navigate("ChapterDetail", { missionId, chapterId: 1 })); }}
              >
                <Ionicons name="play-circle" size={16} color="#FFF" />
                <Text style={styles.primaryBtnText}>{t('missionDetail.continue')}</Text>
              </TouchableOpacity>
            )}
            {isTrial && !isActive && (
              <>
                <TouchableOpacity
                  style={[styles.primaryBtn, { backgroundColor: colors.primary }]}
                  onPress={() => { void launch(missionId, () => navigation.navigate("ChapterDetail", { missionId, chapterId: 1 })); }}
                >
                  <Ionicons name="play-circle" size={16} color="#FFF" />
                  <Text style={styles.primaryBtnText}>{t('mission.openTrial')}</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.secondaryBtn, { backgroundColor: inCart ? colors.success : colors.secondary }]}
                  onPress={() => { if (!inCart) addToCart(missionId); }}
                  disabled={inCart}
                >
                  <Ionicons name={inCart ? "checkmark" : "cart"} size={16} color="#FFF" />
                  <Text style={styles.primaryBtnText}>{t(inCart ? 'mission.inCart' : 'card.buy')}</Text>
                </TouchableOpacity>
              </>
            )}
            {!isActive && !isTrial && (
              <>
                <TouchableOpacity
                  // Même palette que « Start Trial » sur une carte du Marketplace : fond
                  // secondaire + bord discret, et non un bouton cerné de violet qui rivalisait
                  // avec l'action principale.
                  style={[styles.outlineBtn, { backgroundColor: inTrial ? colors.success + "22" : colors.surfaceSecondary,
                                               borderColor: inTrial ? colors.success : colors.borderSecondary }]}
                  onPress={() => { if (!inTrial) void beginTrial(); }}
                  disabled={inTrial}
                >
                  <Ionicons name={inTrial ? "checkmark-circle" : "play"} size={16} color={inTrial ? colors.success : colors.textPrimary} />
                  <Text style={[styles.outlineBtnText, { color: inTrial ? colors.success : colors.textPrimary }]}>
                    {/* ⚠️ TWO HARD-CODED LABELS in an otherwise translated screen. The catalogue
                        already carried `card.startTrial` ("Démarrer l'essai"): nothing was
                        missing, the key simply was never called — so a French visitor read
                        "Start Trial" right next to "Acheter". */}
                    {inTrial ? t('card.inTrial') : t('card.startTrial')}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.primaryBtn, { backgroundColor: inCart ? colors.success : colors.primary }]}
                  onPress={() => { if (!inCart) addToCart(missionId); }}
                  disabled={inCart}
                >
                  <Ionicons name={inCart ? "checkmark" : "cart"} size={16} color="#FFF" />
                  <Text style={styles.primaryBtnText}>{t(inCart ? 'mission.inCart' : 'card.buy')}</Text>
                </TouchableOpacity>
              </>
            )}
          </View>

          {/* Ce que la mission exige du joueur AVANT d'être jouée (CCT-327) : un contrôleur par
              ligne, son état, et un dialogue pour l'activer — celui d'Azure porte le lien de
              déploiement. Remplace les deux panneaux séparés (souscription Azure, dépôt GitHub),
              qui n'annonçaient rien tant que le joueur ne descendait pas jusqu'à eux. */}
          <MissionControllers missionId={missionId} requiresP1={mission.requires_entra_p1} />

          {gestion && (
            // ⚠️ AUTHORS ONLY, AND BOTH GESTURES DESTROY STATE. "Reload" erases progress and
            // leaves the accesses alone: that is the everyday gesture when verifying a scenario,
            // since re-granting a GitHub App between two passes costs minutes and proves nothing
            // about the story under test. "Reset" also forgets the accesses, which is the only
            // way to verify the activation screen itself.
            <View style={{ marginTop: 18, padding: 14, borderRadius: 12, gap: 10,
                           borderWidth: 1, borderColor: colors.border,
                           backgroundColor: colors.cardBackground }}>
              <Text style={{ color: colors.textPrimary, fontWeight: "600" }}>
                {t('mission.manager.title')}
              </Text>
              <Text style={{ color: colors.textSecondary, fontSize: 12.5, lineHeight: 18 }}>
                {t('mission.manager.help')}
              </Text>
              <View style={{ flexDirection: "row", gap: 10, flexWrap: "wrap" }}>
                <TouchableOpacity onPress={() => setReinit("recharger")} disabled={enCours}
                  style={{ flexDirection: "row", alignItems: "center", gap: 6, paddingVertical: 8,
                           paddingHorizontal: 12, borderRadius: 8, borderWidth: 1,
                           borderColor: colors.border, opacity: enCours ? 0.5 : 1 }}>
                  <Ionicons name="refresh" size={15} color={colors.textPrimary} />
                  <Text style={{ color: colors.textPrimary, fontSize: 13 }}>
                    {t('mission.manager.reload')}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity onPress={() => setReinit("reinitialiser")} disabled={enCours}
                  style={{ flexDirection: "row", alignItems: "center", gap: 6, paddingVertical: 8,
                           paddingHorizontal: 12, borderRadius: 8, borderWidth: 1,
                           borderColor: "#b45309", opacity: enCours ? 0.5 : 1 }}>
                  <Ionicons name="nuclear" size={15} color="#b45309" />
                  <Text style={{ color: "#b45309", fontSize: 13 }}>
                    {t('mission.manager.reset')}
                  </Text>
                </TouchableOpacity>
              </View>
            </View>
          )}
        </View>
      </ScrollView>

      {/* ⚠️ BOTH GESTURES ARE CONFIRMED, AND THE LOSS IS STATED BEFOREHAND (CCT-341). "Reload"
          erases progress; "Reset" additionally erases accesses that will have to be granted
          again. Announcing afterwards is not announcing. The reset wording also says what we do
          NOT do: the GitHub App and the Azure delegation live in the player's own accounts, and
          we do not remove them on their behalf. */}
      {!!reinit && (
        <TouchableOpacity activeOpacity={1} onPress={() => !enCours && setReinit(null)}
          style={{ position: "absolute", top: 0, right: 0, bottom: 0, left: 0,
            backgroundColor: "rgba(0,0,0,0.6)", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <View style={{ width: "100%", maxWidth: 460, borderRadius: 16, padding: 20, gap: 14,
            backgroundColor: colors.cardBackground, borderWidth: 1, borderColor: colors.border }}>
            <Text style={{ color: colors.textPrimary, fontWeight: "700", fontSize: 16 }}>
              {t(reinit === "recharger" ? 'mission.manager.reload' : 'mission.manager.reset')}
            </Text>
            <Text style={{ color: colors.textSecondary, fontSize: 13, lineHeight: 19 }}>
              {t(reinit === "recharger"
                ? 'mission.manager.confirmReload'
                : 'mission.manager.confirmReset')}
            </Text>
            <View style={{ flexDirection: "row", gap: 10, justifyContent: "flex-end" }}>
              <TouchableOpacity onPress={() => setReinit(null)} disabled={enCours}
                style={{ paddingVertical: 8, paddingHorizontal: 14, borderRadius: 8,
                         borderWidth: 1, borderColor: colors.border }}>
                <Text style={{ color: colors.textSecondary, fontSize: 13 }}>{t('common.cancel')}</Text>
              </TouchableOpacity>
              <TouchableOpacity disabled={enCours}
                onPress={() => {
                  const avecControleurs = reinit === "reinitialiser";
                  setEnCours(true);
                  void managerReset(missionId, avecControleurs)
                    .catch(() => null)
                    .then(() => { setEnCours(false); setReinit(null); });
                }}
                style={{ paddingVertical: 8, paddingHorizontal: 14, borderRadius: 8,
                         backgroundColor: reinit === "recharger" ? colors.primary : "#b45309",
                         opacity: enCours ? 0.6 : 1 }}>
                <Text style={{ color: "#FFF", fontSize: 13, fontWeight: "600" }}>
                  {t('common.confirm')}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </TouchableOpacity>
      )}

      {/* FICHE du personnage : les cartes ne montraient qu'un nom tronqué et un rôle coupé ;
          la description authorée n'était visible nulle part dans l'app. */}
      {!!charCard && (
        <TouchableOpacity activeOpacity={1} onPress={() => setCharCard(null)}
          style={{ position: "absolute", top: 0, right: 0, bottom: 0, left: 0,
            backgroundColor: "rgba(0,0,0,0.6)", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <View style={{ width: "100%", maxWidth: 420, borderRadius: 16, padding: 20, gap: 10,
            backgroundColor: colors.cardBackground, borderWidth: 1, borderColor: colors.border }}>
            <View style={{ flexDirection: "row", alignItems: "center", gap: 12 }}>
              {/* Fiche du JOUEUR : son avatar et son nom ; le personnage n'est plus que le
                  rôle qu'il tient. Ouvrir « Samir » revenait à lui présenter un inconnu. */}
              {(charCard.isPlayer ? (playerAvatar ?? charCard.avatar) : charCard.avatar)
                ? <Image source={charCard.isPlayer ? (playerAvatar ?? charCard.avatar) : charCard.avatar}
                    style={styles.charAvatar} />
                : null}
              <View style={{ flex: 1 }}>
                <Text style={[styles.sectionTitle, { color: colors.textPrimary, marginBottom: 2 }]}>
                  {charCard.isPlayer ? (playerName || "Vous") : charCard.name}
                </Text>
                <Text style={{ color: colors.textSecondary, fontSize: 13 }}>{charCard.role}</Text>
                {!!charCard.isPlayer && (
                  <Text style={{ color: colors.primary, fontSize: 12, marginTop: 2 }}>
                    dans le rôle de {charCard.name}
                  </Text>
                )}
              </View>
            </View>
            {!!charCard.description && (
              <Text style={{ color: colors.textSecondary, fontSize: 13, lineHeight: 19 }}>{charCard.description}</Text>
            )}
            <TouchableOpacity onPress={() => setCharCard(null)} style={{ alignSelf: "flex-end", padding: 8 }}>
              <Text style={{ color: colors.primary, fontWeight: "600" }}>{t('common.close')}</Text>
            </TouchableOpacity>
          </View>
        </TouchableOpacity>
      )}
      {launchDialog}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  backBtn: {
    position: "absolute",
    top: 48,
    left: 16,
    zIndex: 10,
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(0,0,0,0.4)",
  },
  // Pas de `maxHeight` : il casserait le ratio du cadre et ferait rogner l'image.
  banner: { width: "100%" as any },
  bannerPlaceholder: { width: "100%" as any, height: 180, alignItems: "center", justifyContent: "center" },
  bannerAvatarRow: { flexDirection: "row", alignItems: "center", justifyContent: "center" },
  bannerAvatarCircle: { width: 64, height: 64, borderRadius: 32, overflow: "hidden", borderWidth: 3, borderColor: "rgba(255,255,255,0.3)" },
  bannerAvatarOverlap: { marginLeft: -18 },
  bannerAvatarImg: { width: 64, height: 64, borderRadius: 32 },
  body: { padding: 20, paddingBottom: 40 },
  titleRow: { flexDirection: "row", alignItems: "flex-start", marginBottom: 20, gap: 12 },
  missionTitle: { fontSize: 26, fontWeight: "bold", marginBottom: 4 },
  subtitle: { fontSize: 14, fontStyle: "italic", lineHeight: 20 },
  certBadge: { paddingHorizontal: 10, paddingVertical: 5, borderRadius: 8, alignSelf: "flex-start", marginTop: 4 },
  certBadgeText: { color: "#FFF", fontSize: 12, fontWeight: "700" },
  section: { paddingBottom: 20, marginBottom: 20, borderBottomWidth: 1 },
  sectionTitle: { fontSize: 18, fontWeight: "700", marginBottom: 12 },
  synopsisPara: { fontSize: 14, lineHeight: 22, marginBottom: 10 },
  charsGrid: { flexDirection: "row", flexWrap: "wrap", gap: 16 },
  charItem: { alignItems: "center", width: 72 },
  // Cerclé : c'est le personnage que le joueur INCARNE (CCT-151).
  charAvatarMe: { borderWidth: 2, borderColor: "#22D3EE" } as any,
  charAvatar: { width: 60, height: 60, borderRadius: 30, marginBottom: 6 },
  charName: { fontSize: 12, fontWeight: "600", textAlign: "center" },
  charRole: { fontSize: 10, textAlign: "center", lineHeight: 14, marginTop: 2 },
  objRow: { flexDirection: "row", gap: 10, marginBottom: 8, alignItems: "flex-start" },
  objText: { flex: 1, fontSize: 14, lineHeight: 20 },
  // Largeur BORNÉE : `flex: 1` sur chaque bouton dans un conteneur pleine largeur donnait, sur
  // un écran de 1600 px, deux boutons de 750 px de large.
  actions: { flexDirection: "row", gap: 8, flexWrap: "wrap", maxWidth: 520 },
  // Mêmes métriques que les boutons d'une carte du Marketplace (`MissionsScreen`) : la page de
  // détails avait ses propres valeurs (13 px de haut, rayon 10, gras 700), et ses boutons
  // s'étiraient sur toute la largeur de l'écran — deux barres énormes pour deux actions.
  primaryBtn: { flex: 1, flexDirection: "row", alignItems: "center", justifyContent: "center", paddingVertical: 10, borderRadius: 8, gap: 6 },
  primaryBtnText: { color: "#FFF", fontSize: 14, fontWeight: "600" },
  secondaryBtn: { flex: 1, flexDirection: "row", alignItems: "center", justifyContent: "center", paddingVertical: 13, borderRadius: 10, gap: 8 },
  outlineBtn: { flex: 1, flexDirection: "row", alignItems: "center", justifyContent: "center", paddingVertical: 10, borderRadius: 8, borderWidth: 1, gap: 6 },
  outlineBtnText: { fontSize: 14, fontWeight: "600" },
});
